/**
 * User Management Component
 * Tabbed interface for Platform Members and Newsletter Subscribers
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  useTheme,
} from '@mui/material';
import {
  People as PeopleIcon,
  Email as EmailIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import PlatformMembersTab from './PlatformMembersTab';
import FreeTierMembersTab from './FreeTierMembersTab';
import NewsletterSubscribersTab from './NewsletterSubscribersTab';
import { colors, spacing } from '../../theme/designTokens';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-management-tabpanel-${index}`}
      aria-labelledby={`user-management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const TAB_COLORS = ['#1A5F7A', '#D97706', '#c2410c'];

export const UserManagement: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);

  // Set initial tab from navigation state
  useEffect(() => {
    const state = location.state as { initialTab?: number };
    if (state?.initialTab !== undefined) {
      setTabValue(state.initialTab);
    }
  }, [location.state]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const activeColor = TAB_COLORS[tabValue] || TAB_COLORS[0];

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Typography variant="h1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: spacing[3], mb: spacing[2] }}>
          <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
          Analytics and management for platform members and newsletter subscribers
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: activeColor, mb: spacing[6] }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{
            style: {
              backgroundColor: activeColor,
              height: 3,
            }
          }}
        >
          <Tab
            label="Platform Members"
            icon={<PeopleIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '& .MuiTab-iconWrapper': {
                marginRight: spacing[2],
                marginBottom: 0
              },
              '&.Mui-selected': {
                color: TAB_COLORS[0],
              }
            }}
          />
          <Tab
            label="Free Tier Members"
            icon={<PersonOffIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '& .MuiTab-iconWrapper': {
                marginRight: spacing[2],
                marginBottom: 0
              },
              '&.Mui-selected': {
                color: TAB_COLORS[1],
              }
            }}
          />
          <Tab
            label="Newsletter Subscribers"
            icon={<EmailIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '& .MuiTab-iconWrapper': {
                marginRight: spacing[2],
                marginBottom: 0
              },
              '&.Mui-selected': {
                color: TAB_COLORS[2],
              }
            }}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <PlatformMembersTab />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <FreeTierMembersTab />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <NewsletterSubscribersTab />
      </TabPanel>
    </Box>
  );
};

export default UserManagement;
